import React, { useState, useMemo } from 'react';

/**
 * Modal for creating/editing a master item or bundle.
 * Saves atomically via manage_master_item RPC.
 */
const MasterItemEditModal = ({
  item,
  isBundle,
  allItems,
  spaceAssignments,
  homeChoiceConfig,
  referenceData,
  onSave,
  onClose,
}) => {
  const { itemTypes, costTypes, phases, vendorTypes, bbChapters, spaces, constructionTypes } =
    referenceData;

  // ─── Form state ──────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    itemName: item?.item_name || '',
    itemTypeId: item?.item_type_id || '',
    costTypeId: item?.cost_type_id || '',
    projectPhaseId: item?.project_phase_id || '',
    estimatedCost: item?.estimated_cost || '',
    notes: item?.notes || '',
    bbChapterId: item?.bb_chapter_id || '',
    vendorTypeId: item?.vendor_type_id || '',
    includeInBoq: item?.include_in_boq || false,
    requiresGcPrep: item?.requires_gc_prep || false,
    bundleType: item?.bundle_type || 'simple',
    bundleChildIds: (item?.bundle_items?.items || []).map(c => c.item_id),
    boqItems: item?.boq_items || [],
    selectedSpaceIds: spaceAssignments?.map(sa => sa.master_space_id) || [],
    showInHomeChoices: !!homeChoiceConfig,
    homeChoiceScope: homeChoiceConfig?.scope || 'whole_home',
    homeChoiceConstructionTypeId: homeChoiceConfig?.construction_type_id || '',
    homeChoiceDisplayOrder: homeChoiceConfig?.display_order || 0,
  });
  const [childSearch, setChildSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // ─── BB chapter hierarchy ────────────────────────────────────────
  const sortedChapters = useMemo(() => {
    if (!bbChapters) return [];
    return [...bbChapters].sort((a, b) => a.code.localeCompare(b.code));
  }, [bbChapters]);

  // ─── Vendor types grouped by category ────────────────────────────
  const vendorTypesByCategory = useMemo(() => {
    if (!vendorTypes) return [];
    const groups = {};
    vendorTypes.forEach(vt => {
      const cat = vt.category || 'אחר';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(vt);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, 'he'));
  }, [vendorTypes]);

  // ─── Bundle child items (exclude self) ───────────────────────────
  const availableChildren = useMemo(() => {
    if (!allItems) return [];
    return allItems
      .filter(
        i => i.id !== item?.id && i.item_name.toLowerCase().includes(childSearch.toLowerCase()),
      )
      .slice(0, 50);
  }, [allItems, item?.id, childSearch]);

  // ─── Save ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.itemName.trim()) {
      setError('נא להזין שם פריט');
      return;
    }
    if (!formData.itemTypeId) {
      setError('נא לבחור סוג פריט');
      return;
    }
    if (!formData.costTypeId) {
      setError('נא לבחור סוג עלות');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSave({
        p_item_id: item?.id || null,
        p_item_name: formData.itemName.trim(),
        p_item_type_id: formData.itemTypeId,
        p_cost_type_id: formData.costTypeId,
        p_project_phase_id: formData.projectPhaseId || null,
        p_estimated_cost: formData.estimatedCost ? Number(formData.estimatedCost) : null,
        p_notes: formData.notes || null,
        p_bb_chapter_id: formData.bbChapterId || null,
        p_vendor_type_id: formData.vendorTypeId || null,
        p_include_in_boq: formData.includeInBoq,
        p_requires_gc_prep: formData.requiresGcPrep,
        p_is_bundle: isBundle,
        p_bundle_type: isBundle ? formData.bundleType : null,
        p_bundle_items: isBundle
          ? {
              items: formData.bundleChildIds.map(id => ({
                item_id: id,
              })),
            }
          : null,
        p_item_options: null,
        p_boq_items: formData.boqItems || [],
        p_space_ids: formData.selectedSpaceIds,
        p_home_choice_scope: formData.showInHomeChoices ? formData.homeChoiceScope : null,
        p_home_choice_construction_type_id: formData.showInHomeChoices
          ? formData.homeChoiceConstructionTypeId || null
          : null,
        p_home_choice_display_order: formData.showInHomeChoices
          ? formData.homeChoiceDisplayOrder
          : 0,
      });
    } catch (err) {
      setError(err.message || 'שגיאה בשמירה');
      setSaving(false);
    }
  };

  const toggleSpace = spaceId => {
    setFormData(prev => ({
      ...prev,
      selectedSpaceIds: prev.selectedSpaceIds.includes(spaceId)
        ? prev.selectedSpaceIds.filter(id => id !== spaceId)
        : [...prev.selectedSpaceIds, spaceId],
    }));
  };

  const addChild = childId => {
    if (!formData.bundleChildIds.includes(childId)) {
      setField('bundleChildIds', [...formData.bundleChildIds, childId]);
    }
  };

  const removeChild = childId => {
    setField(
      'bundleChildIds',
      formData.bundleChildIds.filter(id => id !== childId),
    );
  };

  const getChildName = id => allItems?.find(i => i.id === id)?.item_name || id;

  return (
    <div className="item-manager-modal-overlay" onClick={onClose}>
      <div className="item-manager-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="item-manager-modal-header">
          <h2>
            {item
              ? `עריכת ${isBundle ? 'חבילה' : 'פריט'}`
              : `${isBundle ? 'חבילה חדשה' : 'פריט חדש'}`}
          </h2>
          <button className="item-manager-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="item-manager-modal-body">
          {error && <div className="item-manager-error">{error}</div>}

          {/* Core fields */}
          <div className="item-manager-form-section">
            <h3>פרטי פריט</h3>
            <div className="item-manager-form-grid">
              <div className="item-manager-form-field">
                <label>
                  שם פריט <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.itemName}
                  onChange={e => setField('itemName', e.target.value)}
                />
              </div>
              <div className="item-manager-form-field">
                <label>
                  סוג פריט <span className="required">*</span>
                </label>
                <select
                  value={formData.itemTypeId}
                  onChange={e => setField('itemTypeId', e.target.value)}
                >
                  <option value="">בחר...</option>
                  {itemTypes?.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="item-manager-form-field">
                <label>
                  סוג עלות <span className="required">*</span>
                </label>
                <select
                  value={formData.costTypeId}
                  onChange={e => setField('costTypeId', e.target.value)}
                >
                  <option value="">בחר...</option>
                  {costTypes?.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="item-manager-form-field">
                <label>שלב פרויקט</label>
                <select
                  value={formData.projectPhaseId}
                  onChange={e => setField('projectPhaseId', e.target.value)}
                >
                  <option value="">בחר...</option>
                  {phases?.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="item-manager-form-field">
                <label>עלות משוערת</label>
                <input
                  type="number"
                  value={formData.estimatedCost}
                  onChange={e => setField('estimatedCost', e.target.value)}
                />
              </div>
            </div>
            <div className="item-manager-form-grid full" style={{ marginTop: 12 }}>
              <div className="item-manager-form-field">
                <label>הערות</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setField('notes', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Bundle config */}
          {isBundle && (
            <div className="item-manager-form-section">
              <h3>הגדרות חבילה</h3>
              <div style={{ marginBottom: 12 }}>
                <label style={{ marginLeft: 16 }}>
                  <input
                    type="radio"
                    name="bundleType"
                    value="simple"
                    checked={formData.bundleType === 'simple'}
                    onChange={() => setField('bundleType', 'simple')}
                  />{' '}
                  חבילה פשוטה
                </label>
                <label>
                  <input
                    type="radio"
                    name="bundleType"
                    value="choice"
                    checked={formData.bundleType === 'choice'}
                    onChange={() => setField('bundleType', 'choice')}
                  />{' '}
                  חבילת בחירה
                </label>
              </div>

              {/* Selected children */}
              {formData.bundleChildIds.length > 0 && (
                <div className="item-manager-selected-children">
                  {formData.bundleChildIds.map(id => (
                    <span key={id} className="item-manager-child-tag">
                      {getChildName(id)}
                      <button onClick={() => removeChild(id)}>×</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Child picker */}
              <div className="item-manager-child-picker" style={{ marginTop: 8 }}>
                <input
                  type="text"
                  className="item-manager-child-search"
                  placeholder="חפש פריט להוספה..."
                  value={childSearch}
                  onChange={e => setChildSearch(e.target.value)}
                />
                <div className="item-manager-child-list">
                  {availableChildren.map(ci => (
                    <div
                      key={ci.id}
                      className="item-manager-child-item"
                      onClick={() => addChild(ci.id)}
                      style={{
                        opacity: formData.bundleChildIds.includes(ci.id) ? 0.4 : 1,
                      }}
                    >
                      {ci.item_name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Classifications */}
          <div className="item-manager-form-section">
            <h3>סיווגים</h3>
            <div className="item-manager-form-grid">
              <div className="item-manager-form-field">
                <label>פרק כחול (BB)</label>
                <select
                  value={formData.bbChapterId}
                  onChange={e => setField('bbChapterId', e.target.value)}
                >
                  <option value="">ללא</option>
                  {sortedChapters.map(ch => (
                    <option key={ch.id} value={ch.id}>
                      {'\u00A0'.repeat(ch.depth * 4)}
                      {ch.depth === 0
                        ? `${ch.code} - ${ch.friendly_name || ch.name_he}`
                        : `${ch.code} ${ch.friendly_name || ch.name_he}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="item-manager-form-field">
                <label>סוג ספק</label>
                <select
                  value={formData.vendorTypeId}
                  onChange={e => setField('vendorTypeId', e.target.value)}
                >
                  <option value="">ללא</option>
                  {vendorTypesByCategory.map(([category, types]) => (
                    <optgroup key={category} label={category}>
                      {types.map(vt => (
                        <option key={vt.id} value={vt.id}>
                          {vt.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 24,
                marginTop: 12,
              }}
            >
              <label className="item-manager-checkbox-row">
                <input
                  type="checkbox"
                  checked={formData.includeInBoq}
                  onChange={e => setField('includeInBoq', e.target.checked)}
                />
                כלול בכתב כמויות
              </label>
              <label className="item-manager-checkbox-row">
                <input
                  type="checkbox"
                  checked={formData.requiresGcPrep}
                  onChange={e => setField('requiresGcPrep', e.target.checked)}
                />
                דורש הכנת קבלן
              </label>
            </div>
          </div>

          {/* Spaces */}
          <div className="item-manager-form-section">
            <h3>חללים מומלצים ({formData.selectedSpaceIds.length})</h3>
            <div className="item-manager-space-grid">
              {spaces?.map(sp => (
                <label key={sp.id} className="item-manager-space-check">
                  <input
                    type="checkbox"
                    checked={formData.selectedSpaceIds.includes(sp.id)}
                    onChange={() => toggleSpace(sp.id)}
                  />
                  {sp.name}
                </label>
              ))}
            </div>
          </div>

          {/* Home Choices */}
          <div className="item-manager-form-section">
            <h3>בחירות לבית</h3>
            <label className="item-manager-checkbox-row">
              <input
                type="checkbox"
                checked={formData.showInHomeChoices}
                onChange={e => setField('showInHomeChoices', e.target.checked)}
              />
              הצג בבחירות לבית
            </label>
            {formData.showInHomeChoices && (
              <div className="item-manager-form-grid" style={{ marginTop: 12 }}>
                <div className="item-manager-form-field">
                  <label>היקף</label>
                  <select
                    value={formData.homeChoiceScope}
                    onChange={e => setField('homeChoiceScope', e.target.value)}
                  >
                    <option value="whole_home">כלל הבית</option>
                    <option value="floor_specific">לפי קומה</option>
                  </select>
                </div>
                <div className="item-manager-form-field">
                  <label>סוג בנייה (אופציונלי)</label>
                  <select
                    value={formData.homeChoiceConstructionTypeId}
                    onChange={e => setField('homeChoiceConstructionTypeId', e.target.value)}
                  >
                    <option value="">הכל</option>
                    {constructionTypes?.map(ct => (
                      <option key={ct.id} value={ct.id}>
                        {ct.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="item-manager-form-field">
                  <label>סדר תצוגה</label>
                  <input
                    type="number"
                    value={formData.homeChoiceDisplayOrder}
                    onChange={e => setField('homeChoiceDisplayOrder', Number(e.target.value))}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="item-manager-modal-footer">
          <button className="item-manager-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
          <button className="item-manager-btn" onClick={onClose} disabled={saving}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

export default MasterItemEditModal;
